"use client";
import React, { useEffect, useState } from "react";
import { AppRouterCacheProvider } from "@mui/material-nextjs/v13-appRouter";
import { AntdRegistry } from "@ant-design/nextjs-registry";
import Clarity from "@microsoft/clarity";
import { Grid } from "antd";
import { load } from "@fingerprintjs/botd";
import { useLocalStorage } from "@/components/detailed-form/utils/hooks";
import { ThemeProvider } from "@mui/material/styles";
import theme, { darkTheme } from "./theme.js";
import Header from "@/components/header/index";
import Script from "next/script.js";
import { ToastProvider } from "@/components/common/toast/index.js";
import Footer from "@/components/footer-primary/index.js";
import { WebVitals } from "@/components/web-vitals";
import { usePathname, useRouter } from "next/navigation";
import {
  DISCORD_BOTS,
  triggerDiscordMessage,
  extractUtmParams,
  generateSession,
  metadataMap,
  handleClaimUpsert,
  notBlacklistedRoute,
} from "./utils/functions.js";
import { initMixpanel } from "./lib/mixpanelClient.js";
import "./globals.css";
import styles from "./page.module.css";
import { FeedbackDialogProvider } from "@/context/feedbackDialog.context.js";
import "bootstrap/dist/css/bootstrap.min.css";
import { ConfigProvider } from "antd";
import { antDTheme, antDMobileTheme } from "./theme-antd.js";
import "./ui.scss";
import FeedbackButton from "@/components/feedback-button/index.js";
import FeedbackDialog from "@/components/feedback-popup/index.js";
import ScrollToTop from "@/components/scroll-to-top";

// export const dynamic = "force-dynamic";

const metadata1 = {
  title:
    "Fair.inc | PCP Claims | Mis-Sold Car Finance Claims | Mis-Sold HP Finance",
  description:
    "Are you eligible to submit PCP claims or HP claims for any mis-sold car finance agreement? Check If you have been Mis-sold PCP or HP finance.",
  viewport: "width=device-width, initial-scale=1, maximum-scale=1",
  openGraph: {
    url: "https://www.fair.inc/",
    title: "Fair.inc | Claim Mis-Sold PCP & HP Car Finance Refunds",
    description:
      "Recover £1000s from mis-sold PCP and HP car finance with Fair.inc. Quick 60-second check, no cost, no hassle. Trusted experts since 2008.",
    type: "website",
  },
};
let renderFlag = 0;
export default function RootLayout({ children }) {
  const currentHost =
    typeof window !== "undefined" ? window.location.hostname : "";
  const currentHref = typeof window !== "undefined" ? window.location.href : "";
  const pathname = usePathname();
  const router = useRouter();

  const { saveToStorage, loadFromStorage } = useLocalStorage();
  const gtmIds = {
    "find.fair.inc": "GTM-M5CMHQ84",
    "check.fair.inc": "GTM-5ZBNR54P",
    "new.fair.inc": "GTM-5ZBNR54P",
    "reclaims.fair.inc": "GTM-PZRLT72K",
    "mail.fair.inc": "GTM-MVLW497F",
    "connect.fair.inc": "GTM-5T8RT6CG",
    "direct.fair.inc": "GTM-5MMK47GQ",
    "claim.fair.inc": "GTM-P86W95TH",
    "submit.fair.inc": "GTM-P86W95TH",
  };

  useEffect(() => {
    let LogRocketInstance;
    if (notBlacklistedRoute(pathname)) {
      import("logrocket").then((module) => {
        LogRocketInstance = module.default;
        LogRocketInstance.init("uu76hz/fairinc", {
          rootHostname: "www.fair.inc", // process.env.PROD_BASE_URL,
        });
      });
    }

    Clarity.init("shvcumraeo");

    const initTiktok = async () => {
      const TiktokPixelModule = await import("tiktok-pixel");
      initTiktokEvents(TiktokPixelModule.default);
    };
    initTiktok();
    const source = window.location.href;
    const isStageOrLocal =
      source?.startsWith("https://www.stage.fair.inc") ||
      source?.startsWith("http://localhost");
    !isStageOrLocal && initMixpanel();
    const referrer = document.referrer || window.location.hostname;
    saveToStorage("referrer", referrer);

    const utmJson = extractUtmParams() || "{}";
    if (notBlacklistedRoute(pathname)) {
      renderFlag === 0 &&
        generateSession(utmJson)
          .then((res) => {
            if (!res) {
              console.log("Unable to generate session");
              if (!pathname?.includes("d-leads")) {
                router.push("/");
              }
            }
          })
          .finally(() => {
            if (!pathname.includes("/404"))
              load()
                .then((botd) => botd.detect())
                .then((result) => {
                  const { bot = false } = result;
                  if (bot) {
                    const claimNumber = loadFromStorage("currentId");
                    triggerDiscordMessage({
                      bot: DISCORD_BOTS.BOT_ALERT,
                      content: `# Claim Number: **${claimNumber}**
                  - Bot detected by BotD:\n\`\`\`${JSON.stringify(
                    result
                  )}\`\`\``,
                    });
                    // window.location.href = '/404';
                  }
                })
                .catch((error) => console.error(error));
          });
      renderFlag = 1;
    }
    console.log("App Version:", "v2.0.0");
    if (window.location.href.includes("get-started-v4")) {
      saveToStorage("theme", "theme-dark");
    } else {
      saveToStorage("theme", "theme-light");
    }
  }, []);

  useEffect(() => {
    const sessionStart = localStorage.getItem("sessionStart");
    const handleUnload = () => {
      if (sessionStart) {
        localStorage.removeItem("sessionStart");
        handleClaimUpsert({
          sessionStart,
        });
      }
    };

    window.addEventListener("beforeunload", handleUnload);

    // Clean up
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, []);

  const initTiktokEvents = (TiktokPixel) => {
    const options = {
      debug: false, // enable logs
    };
    const profile = {
      "www.fair.inc": {
        pixel_id: "CUKOLK3C77UE7K4EL55G", //'COS9M5BC77UBF4EELLT0',
      },
      "direct.fair.inc": {
        pixel_id: "CUN1O2RC77U27ULH8E2G",
      },
      "claim.fair.inc": {
        pixel_id: "COS9M5BC77UBF4EELLT0",
      },
      "submit.fair.inc": {
        pixel_id: "COS9M5BC77UBF4EELLT0",
      },
      "mail.fair.inc": {
        pixel_id: "CUKP91BC77U8E2TJKO10",
      },
      "reclaims.fair.inc": {
        pixel_id: "CUKP8PBC77UE7K4EL5G0",
      },
      "find.fair.inc": {
        pixel_id: "CUKP87JC77UA8JMIP2V0",
      },
      "check.fair.inc": {
        pixel_id: "CUT2Q03C77UE7K4EQK4G",
      },
      "new.fair.inc": {
        pixel_id: "CUT2Q03C77UE7K4EQK4G",
      },
      "connect.fair.inc": {
        pixel_id: "CUKP9BJC77U8E2TJKO20",
      },
    };
    false &&
      TiktokPixel &&
      TiktokPixel.init(
        (profile[window.location.hostname] || profile["www.fair.inc"]).pixel_id,
        {},
        options
      );
    false && TiktokPixel && TiktokPixel.pageView(); // For tracking page view
  };

  const handleLuminateLoaderScript = () => {
    const loaderScript = document.createElement("script");
    loaderScript.src = "/loader_only.js";
    loaderScript.async = true;
    document.body.appendChild(loaderScript);
  };
  // const showFooter = pathname === "/" || pathname === "/claim-assistance" || pathname === "/welcome" || pathname === "/welcome-1"|| pathname === "/start" || pathname === "/start-1"|| pathname === "/start-2"|| pathname === "/start-3"|| pathname === "/start-4";
  const showFooter =
    pathname === "/get-started-v3" ||
    // !pathname.includes("get-started-v3") &&
    // !pathname.includes("get-started") &&
    (!pathname.includes("test-zoho") &&
      !pathname.includes("sign-redo") &&
      !pathname.includes("d-leads") &&
      !pathname.includes("status") &&
      !pathname.includes("call-center"));
  const showHeader =
    !pathname.includes("status") && !pathname.includes("d-leads");
  const showFeedbackButton = pathname.includes("get-started");
  const currentMetadata = metadataMap[pathname] || metadata1;
  // const isDevelopmentOrStaging = currentHost.includes("localhost") || currentHost.includes("staging.") || currentHost.includes("dev.");
  const isDevelopmentOrStaging = currentHost !== "www.fair.inc";

  const minimalHeaderPaths = ["sign-redo", "manual-lender"];
  const showMinimalHeader = minimalHeaderPaths.some((substring) =>
    pathname.includes(substring)
  );
  // const showMinimalHeader = pathname.includes("sign-redo") || pathname.includes("manual-lender");
  const isGetStartedV4 = currentHref.includes("get-started-v4");
  const isGetStartedV3 = currentHref.includes("get-started-v3");
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const activeAntDTheme = screens.xs ? antDMobileTheme : antDTheme;
  return (
    <html lang="en" suppressHydrationWarning={true}>
      <head>
        <title>{currentMetadata.title}</title>
        <meta name="description" content={currentMetadata.description} />
        <link
          rel="preload"
          as="image"
          href="/car-mockup-full.webp"
          imageSrcSet="/car-mockup-full.webp"
          imageSizes="(max-width: 992px) 550px, 0vw"
        />
        <link
          rel="canonical"
          href={
            pathname.includes("mailonline") || pathname.includes("NHS-members")
              ? `https://www.fair.inc/`
              : `https://www.fair.inc${pathname}`
          }
        />
      </head>
      <body
        // style={
        //   isGetStartedV4
        //     ? { background: "#222c42" }
        //     : isGetStartedV3
        //     ? {} //{ background: "#E9F3EB" }
        //     : {}
        // }
        style={{
          fontFamily: `'Poppins', sans-serif !important`
        }}
        suppressHydrationWarning={true}
      >
        <WebVitals />
        <div id="top-banner-main"></div>
        <ScrollToTop />
        <FeedbackDialogProvider>
          <AppRouterCacheProvider>
            <ThemeProvider theme={isGetStartedV4 ? darkTheme : theme}>
              <ConfigProvider theme={activeAntDTheme}>
                <AntdRegistry>
                  {showHeader && (
                    <Header showFeedbackButton={showFeedbackButton} />
                  )}
                  <main id="main-continer" className={styles.main}>
                    {children}
                  </main>
                  {showFooter && <Footer />}
                  {showFeedbackButton && (
                    <div className="fixed bottom-6 right-6 hidden md:block">
                      <FeedbackButton />
                    </div>
                  )}
                  <FeedbackDialog />
                  <ToastProvider></ToastProvider>
                </AntdRegistry>
              </ConfigProvider>
            </ThemeProvider>
          </AppRouterCacheProvider>
        </FeedbackDialogProvider>

        <Script 
          strategy="lazyOnload" async>{`
        const host = window.location.hostname;
        const gtmIdss = {
          "find.fair.inc": "GTM-M5CMHQ84",
          "check.fair.inc": "GTM-5ZBNR54P",
          "new.fair.inc": "GTM-5ZBNR54P",
          "reclaims.fair.inc": "GTM-PZRLT72K",
          "mail.fair.inc": "GTM-MVLW497F",
          "connect.fair.inc": "GTM-5T8RT6CG",
          "direct.fair.inc": "GTM-5MMK47GQ",
          "claim.fair.inc": "GTM-P86W95TH",
          "submit.fair.inc": "GTM-P86W95TH",
        };
        if(!host.includes("localhost")) {
          if(gtmIdss[host]) {
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer',gtmIdss[host]);
          } else {
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-P86W95TH');
          }
        }
        `}</Script>

        <Script
          src={"/iojs_script.js"}
          onLoad={handleLuminateLoaderScript}
          async
          strategy="lazyOnload"
        ></Script>
        {/* <Script async src="https://cdn.polygraph.net/pg.js"></Script> */}
        <noscript>
          {!gtmIds[currentHost] && (
            <iframe
              id="googletagmanager-iframe"
              src={`https://www.googletagmanager.com/ns.html?id=GTM-P86W95TH`}
              height="0"
              width="0"
              style={{ display: "none", visibility: "hidden" }}
            ></iframe>
          )}

          {gtmIds[currentHost] && (
            <iframe
              id="googletagmanager-iframe"
              src={`https://www.googletagmanager.com/ns.html?id=${gtmIds[currentHost]}`}
              height="0"
              width="0"
              style={{ display: "none", visibility: "hidden" }}
            ></iframe>
          )}
        </noscript>
        <Script id="onfido-script" src="https://sdk.onfido.com/v14" async />
        <link
          rel="preload"
          as="image"
          href="/trustpilot_disclaimer.svg"
          imageSrcSet="/trustpilot_disclaimer.svg"
          imageSizes="(max-width: 992px) 223px, 0vw"
        />
      </body>
    </html>
  );
}
